import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import styled from 'styled-components';
import { Button, Table } from '@components/Common';
import CheckMark from '@components/Common/CheckMark';
import { FilterContainer } from '@components/Common/EntryFilters';
import TankSelectorControlled from '@components/Common/EntryFilters/TankSelectorControlled';
import DateFilterControlled from '@components/Common/EntryFilters/DateFilterControlled';
import ContentLoader, { ContentContainer } from '@components/Common/Loaders/ContentLoader';
import { Columns } from '@components/Common/SharedStyles';
import { Layout, MainContent, SideBar } from '@components/Common/SideBarLayout';
import { formatLocalDateTime } from '@helpers/formatting';
import { compareDatesAsc, endOfDayLocal, toIsoUtc } from '@helpers/dateUtils';
import { measurementTimestampSortByString } from '@helpers/sorting';
import { useMeasurementsFilters } from '@hooks/useMeasurementsFilters';
import Modes from '../Common/InventoryNavigation';
import MeasurementEditor from './MeasurementEditor';

// TanStack Query hooks
import { useProductsQuery } from '@api/queries/products';
import { useTanksQuery, Tank } from '@api/queries/tanks';
import { useTerminalsQuery } from '@api/queries/terminals';
import {
  useMeasurementsQuery,
  useCreateMeasurementMutation,
  useUpdateMeasurementMutation,
  useDeleteMeasurementMutation,
  Measurement,
  MeasurementCmd
} from '@api/queries/measurements';

const ButtonColumn = styled.div`
  padding-top: 48px;
`;

const MeasurementList: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Local UI state
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selectedMeasurement, setSelectedMeasurement] = useState<Measurement | null>(null);

  // Track if we should open editor when tank is selected (from ?new=true URL param)
  const pendingNewMeasurement = useRef(false);

  // URL-based filters
  const { filters, tankFilter, dateFilter, setTankFilter, setDateFilter } = useMeasurementsFilters();
  const selectedTankId = filters.tankId;

  // Handle ?new=true URL param to auto-open editor
  // If no tank selected yet, remember that we want to open editor when tank is selected
  useEffect(() => {
    const hasNewParam = searchParams.get('new') === 'true';

    if (hasNewParam) {
      // Remove 'new' param from URL immediately to clean it up
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('new');
      setSearchParams(newParams, { replace: true });

      if (selectedTankId) {
        // Tank is already selected, open editor immediately.
        // Syncing from the URL (external state) is a legitimate effect; deriving this during render
        // would loop until the effect above removes the 'new' param.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSelectedMeasurement(null);
        setIsEditorOpen(true);
      } else {
        // No tank selected yet, remember to open editor when tank is selected
        pendingNewMeasurement.current = true;
      }
    }
  }, [searchParams, selectedTankId, setSearchParams]);

  // Open editor when tank is selected and we have a pending new measurement
  useEffect(() => {
    if (selectedTankId && pendingNewMeasurement.current) {
      pendingNewMeasurement.current = false;
      setSelectedMeasurement(null);
      setIsEditorOpen(true);
    }
  }, [selectedTankId]);

  // TanStack Query for data fetching
  const { data: products = [] } = useProductsQuery();
  const { data: tanks = [] } = useTanksQuery();
  const { data: terminals = [] } = useTerminalsQuery();
  const { data: measurements = [], isLoading: isLoadingMeasurements, refetch } = useMeasurementsQuery(selectedTankId);

  // Mutations
  const createMutation = useCreateMeasurementMutation();
  const updateMutation = useUpdateMeasurementMutation();
  const deleteMutation = useDeleteMeasurementMutation();

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isRemoving = deleteMutation.isPending;

  // Find selected tank
  const selectedTank = useMemo(() => {
    return selectedTankId ? tanks.find(tank => tank.id === selectedTankId) : null;
  }, [selectedTankId, tanks]);

  // Filter measurements by date range
  const filteredMeasurements = useMemo(() => {
    const { startDate, endDate } = dateFilter;
    let filtered = measurements;

    if (startDate) {
      filtered = filtered.filter(e => compareDatesAsc(e.timestamp, startDate) >= 0);
    }

    if (endDate) {
      const endDateNormalized = toIsoUtc(endOfDayLocal(endDate));
      filtered = filtered.filter(e => compareDatesAsc(e.timestamp, endDateNormalized) <= 0);
    }

    return filtered;
  }, [measurements, dateFilter]);

  const formKey = (selectedMeasurement && selectedMeasurement.id) || 'new';
  const showEditor = isEditorOpen && selectedTank && !isLoadingMeasurements;
  const isLoading = isLoadingMeasurements && measurements.length === 0;
  const isRefreshing = isLoadingMeasurements && measurements.length > 0;

  const handleRowSelected = ({ original }: { original: Measurement }) => {
    setSelectedMeasurement(original);
    setIsEditorOpen(true);
  };

  const handleAddClick = () => {
    setSelectedMeasurement(null);
    setIsEditorOpen(true);
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setSelectedMeasurement(null);
  };

  const handleSave = (cmd: MeasurementCmd) => {
    if (cmd.id !== null && cmd.id !== undefined) {
      updateMutation.mutate(cmd, { onSuccess: () => closeEditor() });
    } else {
      createMutation.mutate(cmd, { onSuccess: () => closeEditor() });
    }
  };

  const handleRemove = () => {
    if (selectedMeasurement) {
      deleteMutation.mutate(selectedMeasurement.id, { onSuccess: () => closeEditor() });
    }
  };

  return (
    <>
      <Modes
        isEnabled={!!selectedTankId && !isEditorOpen}
        fetch={() => refetch()}
        isLoading={isRefreshing}
      />
      <FilterContainer>
        <Columns>
          <TankSelectorControlled
            value={tankFilter}
            onChange={setTankFilter}
            terminals={terminals}
            tanks={tanks}
            isDisabled={showEditor}
          />
          <DateFilterControlled value={dateFilter} onChange={setDateFilter} />
          <ButtonColumn>
            <Button tertiary={true} disabled={isSaving || !selectedTank} onClick={handleAddClick}>
              {t('add')} {t('measurements.measurement').toLowerCase()}
            </Button>
          </ButtonColumn>
        </Columns>
      </FilterContainer>
      <Layout>
        <MainContent>
          {selectedTank && (
            <ContentContainer>
              {isLoading && <ContentLoader />}
              <Table
                data={filteredMeasurements}
                columns={[
                  {
                    Header: 'Id',
                    id: 'id',
                    accessor: 'id',
                    show: false
                  },
                  {
                    Header: t('measurements.timestamp'),
                    id: 'timestamp',
                    maxWidth: 170,
                    accessor: 'timestamp',
                    sortMethod: measurementTimestampSortByString,
                    Cell: cellInfo => {
                      const measurement = cellInfo.original;
                      return formatLocalDateTime(measurement.timestamp, true);
                    }
                  },
                  {
                    Header: t('product.product'),
                    id: 'productName',
                    maxWidth: 250,
                    accessor: 'productName'
                  },
                  {
                    Header: () => (
                      <span>
                        {t('measurements.value')} <sup>kg</sup>
                      </span>
                    ),
                    id: 'value',
                    maxWidth: 120,
                    accessor: (measurement: Measurement) =>
                      measurement.value !== null ? measurement.value.toLocaleString('fi') : ''
                  },
                  {
                    Header: t('measurements.affects_balance'),
                    id: 'affectsBalance',
                    width: 150,
                    accessor: 'affectsBalance',
                    Cell: ({ value }) => <CheckMark active={value} />,
                    filterMethod: (filter, row) =>
                      filter.value !== '' ? row[filter.id] === (filter.value === 'true') : true,
                    Filter: ({ filter, onChange }) => (
                      <select
                        onChange={event => onChange(event.target.value)}
                        style={{ width: '100%' }}
                        value={filter ? filter.value : ''}
                      >
                        <option value="" />
                        <option value="true">{t('measurements.filter_affects')}</option>
                        <option value="false">{t('measurements.filter_doesnaffect')}</option>
                      </select>
                    )
                  },
                  {
                    Header: t('measurements.notes'),
                    id: 'notes',
                    accessor: 'notes'
                  }
                ]}
                noDataText={t('entry.no_rows_bookkeeping_measurements')}
                defaultPageSize={20}
                defaultSortColumn="timestamp"
                defaultSortDesc={true}
                onRowSelected={handleRowSelected}
                rowSelector={i => selectedMeasurement && selectedMeasurement.id === i.original.id}
              />
            </ContentContainer>
          )}
        </MainContent>
        {showEditor && selectedTank && (
          <SideBar>
            <MeasurementEditor
              key={formKey}
              measurement={selectedMeasurement}
              tank={selectedTank}
              products={products}
              isSaving={isSaving}
              isRemoving={isRemoving}
              remove={handleRemove}
              close={closeEditor}
              save={handleSave}
            />
          </SideBar>
        )}
      </Layout>
    </>
  );
};

export default MeasurementList;
